import CloseIcon from 'components/ReusableComponents/CloseIcon'
import Dialog from 'components/ReusableComponents/Dialog'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import Gauge from 'features/Engagement/Gauge'
import RevenueTooltip from 'features/MonthlyOTAConversions/RevenueTooltip'
import { useInternalAdmin } from 'hooks'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import complianceApi from 'services/api/compliance'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './ComplianceOptInChartDialog.module.scss'

const ComplianceOptInChartDialog = ({ onClose }) => {
  const [startDate, setStartDate] = useState(moment().add(-29, 'days'))
  const [endDate, setEndDate] = useState(moment())

  const [dataOverTime, setDataOverTime] = useState([])
  const [totalRate, setTotalRate] = useState(0)
  const [totalOptedIn, setTotalOptedIn] = useState(0)
  const [totalSubscribers, setTotalSubscribers] = useState(0)

  const isInternalAdmin = useInternalAdmin()

  useEffect(() => {
    complianceApi
      .optInStats({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
      })
      .then(res => {
        setDataOverTime(res.overTimes)
        setTotalRate(res.rate)
        setTotalOptedIn(res.totalOptedIn)
        setTotalSubscribers(res.totalSubscribers)
      })
  }, [startDate, endDate])

  return (
    <Dialog>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <button onClick={onClose} className={styles.closeButton}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.dialogInner}>
          <div className={styles.gaugeHolder}>
            <div className={styles.chartHeader}>
              <p>Express written consent rate</p>
            </div>
            <div className={styles.gaugeHolderInner}>
              <Gauge percentage={totalRate / 100} />
            </div>
            <p className={styles.gaugeTotalNumber}>
              {formatNumberAsThousands(totalOptedIn)} of{' '}
              {formatNumberAsThousands(totalSubscribers)} guests
            </p>
          </div>
          <div className={styles.chartHolder}>
            <div className={styles.chartHeader}>
              <p>Express written consent rate over time</p>
              <SmartDatepicker
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={setStartDate}
                onChangeEndDate={setEndDate}
                inputClassName={styles.datepickerInput}
                showDatepickerIcon={false}
                smartDates={[
                  smartDateTypes.last7days,
                  smartDateTypes.last14days,
                  smartDateTypes.last30days
                ]}
                showMonths={isInternalAdmin ? true : false}
                showQuarters={isInternalAdmin ? true : false}
              />
            </div>
            <div style={{ height: 240, width: 600, paddingTop: 30 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={dataOverTime}
                  width={500}
                  height={300}
                  margin={{
                    top: 0,
                    right: 15,
                    left: 8,
                    bottom: 0
                  }}
                >
                  <CartesianGrid stroke="#F2F2F2" vertical={false} />
                  <XAxis
                    dataKey="date"
                    axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                    tick={{
                      fill: '#7D7D7D',
                      fontSize: 12,
                      transform: 'translate(0, 14)'
                    }}
                    tickSize={0}
                    padding={{ left: 30, right: 30 }}
                  />
                  <YAxis
                    tickSize={0}
                    axisLine={false}
                    tick={{
                      fill: '#7D7D7D',
                      fontSize: 12,
                      transform: 'translate(-10, 0)'
                    }}
                    tickFormatter={value => `${value}%`}
                    range
                  />
                  <Tooltip
                    content={
                      <RevenueTooltip
                        labelFormatter={label => `${label}`}
                        valueFormatter={value => `%${value}`}
                        dataTitle="Opt-in rate"
                      />
                    }
                    cursor={{
                      stroke: 'red',
                      strokeWidth: 0,
                      fill: '#dedede',
                      opacity: 0.22
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#1B4DFF"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ComplianceOptInChartDialog
